import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handlePhotoSelected, { signal });
    customElements.whenDefined('slideshow-component').then(() => {
      if (!this.isConnected || signal.aborted) return;
      this.setAttribute('data-t2s-photo-navigation-ready', '');
      this.#syncPhotoControl(this.slideshow?.current ?? 0);
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.removeAttribute('data-t2s-photo-navigation-ready');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncPhotoControl(this.slideshow?.current ?? 0);
  }

  /** @param {SlideshowSelectEvent} event */
  #handlePhotoSelected = (event) => {
    if (event.target !== this.slideshow) return;
    this.#syncPhotoControl(event.detail.index);
  };

  /** @param {number} index */
  #syncPhotoControl(index) {
    const control = this.querySelector('[data-t2s-photo-select]');
    if (control instanceof HTMLSelectElement) control.value = String(index);
  }

  /** @param {Event} event */
  selectPhotoFromControl(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    this.selectPhoto(Number(event.target.value), event);
  }

  /**
   * Selects a verified role shortcut or numbered photo in the existing gallery.
   * @param {number} index - Zero-based displayed media index
   * @param {Event} event - The initiating event
   */
  selectPhoto(index, event) {
    if (!Number.isInteger(index) || index < 0) return;
    const grid = this.querySelector('.media-gallery__grid');
    if (grid instanceof HTMLElement && grid.getClientRects().length) {
      const target = grid.children[index];
      if (!(target instanceof HTMLElement)) return;
      target.querySelectorAll('img[loading="lazy"]').forEach((image) => image.setAttribute('loading', 'eager'));
      target.scrollIntoView({ block: 'center', behavior: 'instant' });
      this.#syncPhotoControl(index);
      return;
    }
    const slideshow = this.slideshow;
    const slide = slideshow?.slides?.[index];
    if (!slideshow || !slide) return;
    slide.querySelectorAll('img[loading="lazy"]').forEach((image) => image.setAttribute('loading', 'eager'));
    slideshow.select(index, event, { animate: false });
    this.#syncPhotoControl(index);
    if (event.type === 'click') slideshow.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
